import React, { useState, useEffect } from 'react';
import { Tabs, Table, Button, Input, Space, message, Popconfirm, Card } from 'antd';
import { PlusOutlined, SaveOutlined, DeleteOutlined, TableOutlined } from '@ant-design/icons';
import api from '../../../../services/api';
import './MappingSettings.css';

const { TabPane } = Tabs;

const MappingSettings = ({ user }) => {
    const [shippingStateMappings, setShippingStateMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchShippingStateMappings();
    }, []);

    const fetchShippingStateMappings = async () => {
        setLoading(true);
        try {
            const response = await api.get('/shipping-state-mappings');
            setShippingStateMappings(response.data.map(item => ({ ...item, key: item.id })));
        } catch (error) {
            console.error('Error fetching shipping state mappings:', error);
            message.error('Lỗi khi tải dữ liệu mapping');
        } finally {
            setLoading(false);
        }
    };

    const handleAddRow = () => {
        const newRow = {
            key: `new_${Date.now()}`,
            id: null,
            shippingState: '',
            standardState: '',
            description: '',
            isActive: true,
            isNew: true
        };
        setShippingStateMappings([...shippingStateMappings, newRow]);
    };

    const handleInputChange = (value, record, field) => {
        const newData = shippingStateMappings.map(item => {
            if (item.key === record.key) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setShippingStateMappings(newData);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Validate data
            const invalidRows = shippingStateMappings.filter(
                item => !item.shippingState.trim() || !item.standardState.trim()
            );
            
            if (invalidRows.length > 0) {
                message.error('Vui lòng điền đầy đủ thông tin cho tất cả các dòng');
                setSaving(false);
                return;
            }

            // Prepare data for API
            const dataToSave = shippingStateMappings.map(item => ({
                id: item.isNew ? undefined : item.id,
                shippingState: item.shippingState.trim(),
                standardState: item.standardState.trim(),
                description: item.description || '',
                isActive: item.isActive
            }));

            await api.post('/shipping-state-mappings/bulk-save', {
                mappings: dataToSave
            });

            message.success('Lưu thành công!');
            fetchShippingStateMappings(); // Refresh data
        } catch (error) {
            console.error('Error saving mappings:', error);
            message.error('Lỗi khi lưu dữ liệu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (record) => {
        if (record.isNew) {
            // Just remove from state if it's a new row
            setShippingStateMappings(shippingStateMappings.filter(item => item.key !== record.key));
            return;
        }

        try {
            await api.delete(`/shipping-state-mappings/${record.id}`);
            message.success('Xóa thành công!');
            fetchShippingStateMappings();
        } catch (error) {
            console.error('Error deleting mapping:', error);
            message.error('Lỗi khi xóa dữ liệu');
        }
    };

    const shippingStateColumns = [
        {
            title: 'Shipping State',
            dataIndex: 'shippingState',
            key: 'shippingState',
            render: (text, record) => (
                <Input
                    value={text}
                    onChange={(e) => handleInputChange(e.target.value, record, 'shippingState')}
                    placeholder="Nhập trạng thái shipping"
                />
            ),
        },
        {
            title: 'State Tiêu Chuẩn',
            dataIndex: 'standardState',
            key: 'standardState',
            render: (text, record) => (
                <Input
                    value={text}
                    onChange={(e) => handleInputChange(e.target.value, record, 'standardState')}
                    placeholder="Nhập state tiêu chuẩn"
                />
            ),
        },
        {
            title: 'Mô Tả',
            dataIndex: 'description',
            key: 'description',
            render: (text, record) => (
                <Input
                    value={text}
                    onChange={(e) => handleInputChange(e.target.value, record, 'description')}
                    placeholder="Nhập mô tả (tùy chọn)"
                />
            ),
        },
        {
            title: 'Thao Tác',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Popconfirm
                    title="Bạn có chắc chắn muốn xóa?"
                    onConfirm={() => handleDelete(record)}
                    okText="Có"
                    cancelText="Không"
                >
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
            ),
        },
    ];

    return (
            <Tabs tabPosition='left' defaultActiveKey="shipping-state" >
                <TabPane 
                    tab={<span><TableOutlined /> Shipping State Mapping</span>} 
                    key="shipping-state"
                >
                    <div style={{ marginBottom: 16 }}>
                        <Space>
                            <Button 
                                type='dashed' 
                                icon={<PlusOutlined />} 
                                onClick={handleAddRow}
                            >
                                Thêm Dòng
                            </Button>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />} 
                                onClick={handleSave}
                                loading={saving}
                            >
                                Lưu Tất Cả
                            </Button>
                        </Space>
                    </div>
                    
                    <Table
                        columns={shippingStateColumns}
                        dataSource={shippingStateMappings}
                        loading={loading}
                        pagination={{
                            pageSize: 50,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => 
                                `${range[0]}-${range[1]} của ${total} mục`
                        }}
                        scroll={{ x: 800 }}
                        size="middle"
                    />
                </TabPane>
            </Tabs>
    );
};

export default MappingSettings;
